import React, { useEffect, useRef, useState } from 'react';
import { Download, Library, Plus } from 'lucide-react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { Button } from '@/components/ui/button';
import ActionDrawer from './ActionDrawer';
import { ApplyPlanDialog } from './ApplyPlanDialog';
import ActionsWorkspace from './ActionsWorkspace';
import { INCIDENT } from './ICSConstants';
import PageTitle from '../headers/PageTitle';
import MobileWarningDialog from 'components/DesignSystems/MobileWarning/MobileWarningDialog';
import { useIcsFormPdfPreview } from 'components/IAP/context/IcsFormPdfPreviewContext';
import { useIcsFormFamily } from 'components/IAP/hooks/useIcsFormFamily';
import type { ActionEntity } from './hooks/useScopedActionForm';
import {
  openInstantiatedIncidentTask,
  useInstantiateIncidentTask,
} from './hooks/incidentActionHooks';
import { useHwcgFlag, useUscgFocRequirementsFlag } from 'utils/featureFlags';
import { registerHwcgTourApi } from 'components/IncidentConfig/hwcgTour/hwcgTourBridge';
import {
  HWCG_TOUR_PARAM,
  wantsHwcgTour,
} from 'components/IncidentConfig/hwcgTour/hwcgTourSteps';
import { registerFocPlanningTourApi } from 'components/MapPage/focPlanningTour/focPlanningTourBridge';
import {
  FOC_PLANNING_TOUR_PARAM,
  wantsFocPlanningTour,
} from 'components/MapPage/focPlanningTour/focPlanningTourSteps';

const ActionsPage: React.FC = () => {
  const { incidentId } = useParams<{ incidentId: string }>();
  const [showAddDrawer, setShowAddDrawer] = useState(false);
  const [selectedAction, setSelectedAction] = useState<ActionEntity | null>(
    null
  );
  const { exportAsForm } = useIcsFormPdfPreview();
  const formFamily = useIcsFormFamily();
  const hwcg = useHwcgFlag();
  const uscgFoc = useUscgFocRequirementsFlag();
  const canApplyPlan = hwcg || uscgFoc;
  const { instantiate, isPending: creatingTask } =
    useInstantiateIncidentTask(incidentId);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const [showApplyPlan, setShowApplyPlan] = useState(false);
  const tourStartedRef = useRef(false);
  const focTourStartedRef = useRef(false);
  const wantsTour =
    hwcg && wantsHwcgTour(searchParams.get(HWCG_TOUR_PARAM), 'tasks');
  const wantsFocTour =
    uscgFoc &&
    wantsFocPlanningTour(searchParams.get(FOC_PLANNING_TOUR_PARAM), 'tasks');

  useEffect(
    () =>
      registerHwcgTourApi({
        closeTask: () => setSelectedAction(null),
      }),
    []
  );

  useEffect(
    () =>
      registerFocPlanningTourApi({
        openApplyPlan: () => setShowApplyPlan(true),
        navigateTo: (href) => navigateRef.current(href),
      }),
    []
  );

  useEffect(() => {
    if (!wantsTour || tourStartedRef.current) return;

    const timer = window.setTimeout(() => {
      if (tourStartedRef.current) return;
      tourStartedRef.current = true;
      setSearchParams(
        (params) => {
          params.delete(HWCG_TOUR_PARAM);
          return params;
        },
        { replace: true }
      );
      void import('components/IncidentConfig/hwcgTour/hwcgTour')
        .then(({ startHwcgTour }) => {
          startHwcgTour('tasks', true);
        })
        .catch(() => {});
    }, 400);

    return () => window.clearTimeout(timer);
  }, [wantsTour, setSearchParams]);

  useEffect(() => {
    if (!wantsFocTour) {
      focTourStartedRef.current = false;
      return;
    }
    if (focTourStartedRef.current) return;

    const timer = window.setTimeout(() => {
      if (focTourStartedRef.current) return;
      focTourStartedRef.current = true;
      setSearchParams(
        (params) => {
          params.delete(FOC_PLANNING_TOUR_PARAM);
          return params;
        },
        { replace: true }
      );
      void import('components/MapPage/focPlanningTour/focPlanningTour')
        .then(({ startFocPlanningTour }) => {
          startFocPlanningTour('tasks');
        })
        .catch(() => {});
    }, 400);

    return () => window.clearTimeout(timer);
  }, [wantsFocTour, setSearchParams]);

  const addTask = () => {
    if (!hwcg) {
      setShowAddDrawer(true);
      return;
    }
    if (creatingTask) return;
    void openInstantiatedIncidentTask({
      instantiate,
      status: 'Planned',
      onOpen: setSelectedAction,
    });
  };

  const handleExportICS233 = () => {
    void exportAsForm({
      formname: 'ICS_233_CG',
      data: {},
      mapSketch: null,
    });
  };

  if (!incidentId) {
    return <div>No incident ID provided</div>;
  }

  return (
    <MobileWarningDialog>
      <div className="maincontent dark tw:text-foreground">
        <PageTitle title="Tasks" />
        <ActionsWorkspace
          scope={INCIDENT}
          storageKey="actions-view-preference"
          onEditAction={setSelectedAction}
          toolbar={
            <div className="tw:flex tw:items-center tw:gap-3">
              {formFamily !== 'fema' ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleExportICS233}
                >
                  <Download className="tw:size-4" />
                  Preview ICS 233
                </Button>
              ) : null}
              {canApplyPlan ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  data-tour="hwcg-tasks-apply-plan"
                  onClick={() => setShowApplyPlan(true)}
                >
                  <Library className="tw:size-4" />
                  Apply Plan
                </Button>
              ) : null}
              <Button
                type="button"
                size="sm"
                disabled={creatingTask}
                onClick={addTask}
              >
                <Plus className="tw:size-4" />
                Add Task
              </Button>
            </div>
          }
        />

        {!hwcg && showAddDrawer ? (
          <ActionDrawer
            scope={INCIDENT}
            toggle={() => setShowAddDrawer(false)}
          />
        ) : null}
        {selectedAction && (
          <ActionDrawer
            scope={INCIDENT}
            selectedEntity={selectedAction}
            toggle={() => setSelectedAction(null)}
          />
        )}
        {showApplyPlan ? (
          <ApplyPlanDialog
            incidentId={incidentId}
            onClose={() => setShowApplyPlan(false)}
          />
        ) : null}
      </div>
    </MobileWarningDialog>
  );
};

export default ActionsPage;
